import React, { useState, useEffect, useRef, useCallback } from 'react'
import { theme } from 'theme'
import { Paper, Popper } from '@mui/material'
import { isOverflown } from 'particles/utils'
import { useStyles } from './TagCell'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import LinkButton from 'components/Shared/Button/LinkButton'
import Text from 'particles/Text'
interface GridCellExpandProps {
    value: string
    width?: number
    onClick?: () => void
}
export interface UrlCellProps extends GridRenderCellParams {
    onClick?: () => void
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
    const { width, value, onClick } = props
    const [showFullCell, setShowFullCell] = useState(false)
    const [showPopper, setShowPopper] = useState(false)
    const [clicked, setCLicked] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const wrapper = useRef<HTMLDivElement | null>(null)
    const cellDiv = useRef(null)
    const cellValue = useRef<HTMLDivElement | null>(null)
    const classes = useStyles()

    const handleMouseEnter = () => {
        const isCurrentlyOverflown = cellValue.current ? isOverflown(cellValue.current.childNodes[0] as Element) : false
        setShowPopper(isCurrentlyOverflown)
        setAnchorEl(cellDiv.current)
        setShowFullCell(true)
    }

    const handleMouseLeave = useCallback(() => {
        setShowFullCell(false)
    }, [])

    const handleUpdateClick = useCallback(() => {
        setCLicked(true)
        if (typeof onClick === 'function') {
            onClick()
        }
    }, [onClick])

    useEffect(() => {
        if (!showFullCell) {
            return undefined
        }

        function handleKeyDown(nativeEvent: KeyboardEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [setShowFullCell, showFullCell])

    return (
        <div ref={wrapper} className={classes.root} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div
                ref={cellDiv}
                style={{
                    height: 1,
                    width,
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                }}
            />
            <div
                ref={cellValue}
                className="cellValue"
                style={{
                    overflow: 'hidden',
                }}
            >
                {value ? (
                    <LinkButton
                        href={value != null ? value.toString() : undefined}
                        target="popup"
                        style={{
                            overflow: 'hidden',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            fontSize: theme.newFontSizes.m,
                            fontWeight: theme.fontWeight.medium,
                            lineHeight: theme.lineHeight.l,
                            color: clicked ? theme.newColors.primary500 : theme.newColors.primary300,
                        }}
                        outerControl
                        onClick={handleUpdateClick}
                        onContextMenu={handleUpdateClick}
                        value={value}
                    />
                ) : (
                    <Text scale="bodyS500" color="placeholder">
                        -
                    </Text>
                )}
            </div>
            {showPopper && (
                <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
                    <Paper
                        elevation={1}
                        style={{
                            minHeight: wrapper.current!.offsetHeight - 3,
                            background: theme.newColors.neutral50,
                            width,
                            padding: 8,
                            boxShadow:
                                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        {value ? (
                            <LinkButton
                                href={value != null ? value.toString() : undefined}
                                target="popup"
                                style={{
                                    wordBreak: 'break-all',
                                    fontSize: theme.newFontSizes.m,
                                    fontWeight: theme.fontWeight.medium,
                                    lineHeight: theme.lineHeight.l,
                                    color: clicked ? theme.newColors.primary500 : theme.newColors.primary300,
                                }}
                                outerControl
                                onClick={handleUpdateClick}
                                onContextMenu={handleUpdateClick}
                                value={value}
                            />
                        ) : (
                            <Text scale="bodyS500" color="placeholder">
                                -
                            </Text>
                        )}
                    </Paper>
                </Popper>
            )}
        </div>
    )
})

export const UrlCell = (params: UrlCellProps) => {
    return (
        <GridCellExpand
            value={params.value ? params.value.toString() : ''}
            width={params.colDef.width}
            onClick={params?.onClick}
        />
    )
}
